import * as d3 from 'd3'
import { EventEmitter } from 'events'
import debounce from 'lodash.debounce'
import Renderer from './renderer'
import Viewport from './viewport'
import Adapter from './adapter'
import Force, {
  HOVER_ALPHA,
  HOVER_ALPHA_DECAY,
  HOVER_VELOCITY_DECAY,
  LAYOUT_VELOCITY_DECAY,
  buildDegreeMap,
  buildSiblingMap,
  createSimulation,
} from './force'
import Layout from './layout'
import { type UpdateInputs, dataSignature, hoverOrphaned, isPointerOnly } from './update'
import state from './state'
import { TRANSITION_DURATION } from '../constants'
import { placeMarks } from './marks'
import { drawnLink, drawnNode, oneRoot } from './root'

type UpdateFn = (
  data: MergedData,
  activeId?: string,
  selectedId?: string,
  query?: string
) => void

interface GraphParams {
  svg: SVGSVGElement
  width: number
  height: number
}

export default class Network extends EventEmitter {
  private initialized = false
  private settled = false
  private leaveTimeout?: NodeJS.Timeout
  private settleTimeout?: NodeJS.Timeout
  private pinnedNodeIds = new Set<string>()
  private unpinTimeouts = new Map<string, NodeJS.Timeout>()
  private lastInputs: UpdateInputs = {}
  private saveState = false
  private filtering = false

  private readonly svg: d3.Selection<SVGSVGElement, unknown, null, undefined>
  private readonly simulation: d3.Simulation<GraphNode, GraphLink>
  private readonly renderer: Renderer
  private readonly viewport: Viewport
  private readonly adapter: Adapter
  private readonly force: Force
  private readonly layout: Layout

  constructor({ svg, width, height }: GraphParams) {
    super()
    this.svg = d3.select(svg)

    this.viewport = new Viewport(this.svg, width, height)
    this.renderer = new Renderer(this.viewport.container)
    this.adapter = new Adapter()
    this.force = new Force()
    this.layout = new Layout()

    this.simulation = createSimulation(this.force)
      .on('end', () => {
        // ⚠⚠ NOTHING RE-FRAMES THE GRAPH HERE, AND THAT IS DELIBERATE. The obvious place to fit
        // is at rest, once the extent is final — and it is wrong, because "at rest" is about ten
        // seconds after a cold load. The reader has been looking at the graph for ten seconds by
        // then, and the whole picture changing size with no input of their own is the most
        // alarming thing this page can do. It shipped that way once and was reported immediately.
        //
        // The fit therefore happens in `update`, off the SEEDED positions, which are within a few
        // percent because the rings are fixed radii. A refinement here, gated on a tolerance, was
        // the next attempt and was no better: the springs relax by more than any tolerance worth
        // having, so it still resized the picture ten seconds in, just less. The frame now moves
        // only when the READER does something — a switch, a search, a selection, a resize, or
        // their own wheel. The springs relaxing is not one of those things.
        if (this.saveState && !this.filtering) {
          state.setZoomScale(this.viewport.getCurrentScale())
          state.setPositions(Array.from(this.adapter.nodeMap.values()))
          state.save()
          this.saveState = false
        }
      })
      .on('tick', () => this.tick())

    this.svg
      .call(this.viewport.zoom)
      .on('dblclick.zoom', null)
      .on('dblclick', () => this.center())
      .on('click', event => {
        if (event.target === this.svg.node()) this.emit('close')
      })

    this.center()

    // ⚠ Held so `destroy()` can clear it. A rebuild is routine since app#349, and an uncleared
    // timer keeps every destroyed instance alive for another five seconds.
    this.settleTimeout = setTimeout(() => (this.settled = true), 5000)
    // A font weight is loaded on first use, so a name can be measured in the fallback font and
    // reflow when the file arrives. The map places the marks again on its next move; the graph
    // places them once more when every font is in.
    void document.fonts?.ready.then(() =>
      placeMarks(
        this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point'),
        this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label')
      )
    )
  }

  /**
   * Draw the data, 200 ms debounced with a leading call.
   *
   * ⚠⚠ A FAILED DRAW IS AN `error` EVENT AND NEVER A THROW (app#349). The leading call runs
   * synchronously inside `Graph.tsx`'s effect, and the app has no ErrorBoundary above the graph, so
   * one throw here unmounted the WHOLE page: `#root` empty, the side menu gone with the graph, and
   * no later data change could bring it back. Measured in the harness by making `transform` throw
   * once. A trailing call threw in a timer instead, which left the page up and the graph frozen.
   * Both now report through `error`, and `Graph.tsx` builds a new instance.
   */
  update = debounce<UpdateFn>(
    (...args) => {
      try {
        this.draw(...args)
      } catch (error) {
        // An emitter with no `error` listener THROWS the error, which is the defect again. A draw
        // can still be pending after `destroy` removed the listeners, so report it plainly then.
        if (this.listenerCount('error')) this.emit('error', error)
        else console.error('graph draw failed after the graph was destroyed', error)
      }
    },
    200,
    { leading: true }
  )

  private draw: UpdateFn = (
      data: MergedData,
      activeId?: string,
      selectedId?: string,
      query?: string
    ) => {
      this.filtering = !!query && query.trim().length > 0
      const rerender = this.adapter.transform(oneRoot(data), activeId, selectedId, query)
      const nodes = Array.from(this.adapter.nodeMap.values())
      const links = Array.from(this.adapter.linkMap.values())
      const degrees = buildDegreeMap(links)
      const siblings = buildSiblingMap(links)
      this.force.setDegrees(degrees)
      this.force.setSiblings(siblings)
      // ⚠ EVERY update, not only when positions are seeded. The wedges ARE the layout now — the
      // force holds each node near the bearing its parent gave it — so a graph that gained or
      // lost a node must be re-planned or the survivors keep the shares of a fleet that changed.
      this.force.setAngles(this.layout.plan(nodes, links))

      const shouldLoadPositions = !this.initialized || rerender
      const missingPositions = shouldLoadPositions ? state.loadPositions(nodes) : false
      const needsLayout = rerender || missingPositions

      if (needsLayout) this.layout.seedPositions(nodes)
      if (!this.initialized) this.initialized = true

      // The root is in the simulation and is not drawn (`root.ts`).
      const drawnLinks = links.filter(drawnLink)
      const drawnNodes = nodes.filter(drawnNode)
      this.updateLinks(drawnLinks)
      this.updateBridges(drawnLinks)
      this.updateNodes(drawnNodes)
      this.updateLabels(drawnNodes)
      placeMarks(
        this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point'),
        this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label')
      )

      // Hold a node still while the reader is pointing at it and its services are out, so the
      // thing under the cursor does not walk away from it.
      //
      // ⚠⚠ SCOPED TO THE FOCUSED NODE, AND THAT IS THE WHOLE POINT. This used to pin every node
      // that had an expanded service, which was indistinguishable from "the hovered node" only
      // while hover was the sole thing that could expand one. `Show Services` expands them all at
      // once, so the old rule pinned the ENTIRE graph — and because the release branch compared
      // against a single stored id, every node but one could never be released again: the layout
      // stayed frozen after the switch went off, and `saveState` wrote those frozen positions to
      // localStorage, so it survived a reload. A set, and a focus test, is the fix for both
      // halves.
      //
      // Skip while filtering — the search already force-expands matched services, and pinning
      // the parent stops the reduced tree settling.
      const toPin = new Set<string>()
      if (!this.filtering) {
        nodes.forEach(n => {
          if (n.type === 'service' && n.expanded && n.modelNodeId && this.adapter.focusIds.has(n.modelNodeId)) {
            toPin.add(n.modelNodeId)
          }
        })
      }

      nodes.forEach(n => {
        if (n.type !== 'node' || !n.modelNodeId) return

        if (toPin.has(n.modelNodeId)) {
          // Pin: fix the position and cancel any release already scheduled for it.
          this.pinnedNodeIds.add(n.id)
          const pending = this.unpinTimeouts.get(n.id)
          if (pending) {
            clearTimeout(pending)
            this.unpinTimeouts.delete(n.id)
          }
          if (n.x !== undefined && n.y !== undefined) {
            n.fx = n.x
            n.fy = n.y
          }
        } else if (this.pinnedNodeIds.has(n.id)) {
          // Release, after the services have finished retracting.
          this.pinnedNodeIds.delete(n.id)
          if (!this.unpinTimeouts.has(n.id)) {
            this.unpinTimeouts.set(
              n.id,
              setTimeout(() => {
                if (n.fx === n.x && n.fy === n.y) {
                  n.fx = null
                  n.fy = null
                }
                this.unpinTimeouts.delete(n.id)
              }, TRANSITION_DURATION)
            )
          }
        }
      })

      // While filtering, pin the root account so the reduced tree doesn't drift off-screen
      const accountNode = nodes.find(n => n.type === 'account')
      if (accountNode) {
        if (this.filtering && accountNode.x !== undefined && accountNode.y !== undefined) {
          accountNode.fx = accountNode.x
          accountNode.fy = accountNode.y
        } else if (!this.filtering && accountNode.fx != null && accountNode.fy != null) {
          accountNode.fx = null
          accountNode.fy = null
        }
      }

      const linkForce = this.simulation.force('link') as d3.ForceLink<GraphNode, GraphLink>
      linkForce.links([])
      this.simulation.nodes(nodes)
      linkForce.links(links)

      this.updateParticles(links)

      // ⚠⚠ A HOVER CAN END WITHOUT AN EVENT, so the drawn set has the last word. An element
      // removed under a resting cursor — searched out, switched off, filtered away — raises no
      // `mouseleave`, and the state that named it would otherwise sit there and light the thing
      // up again the moment it comes back. See `hoverOrphaned` (app#316).
      if (hoverOrphaned(nodes, activeId)) this.emit('leave')

      const inputs: UpdateInputs = {
        activeId,
        selectedId,
        query,
        dataSignature: dataSignature(data),
      }
      const pointerOnly = isPointerOnly(this.lastInputs, inputs)
      const activeIdChanged = this.lastInputs.activeId !== activeId
      this.lastInputs = inputs

      if (pointerOnly) {
        // ⚠⚠ A HOVER IS NOT A RE-LAYOUT, AND THE ADAPTER CANNOT SAY SO. Pointing at a node adds
        // its leaves and pointing away removes them, and BOTH arrive here as `rerender` — the
        // same signal a rail switch or a node joining the fleet produces. Taken at face value
        // that gave a hover the full-energy restart meant for a re-layout, and re-framed the
        // viewport on top of it: the whole picture rescaled and re-centred every time the cursor
        // crossed a node. See `isPointerOnly`.
        //
        // So: settle the leaves gently, and leave the frame alone. The reader's orientation is
        // the thing being protected, and it is worth more than a perfectly fitted extent.
        this.saveState = true
        this.simulation
          .velocityDecay(HOVER_VELOCITY_DECAY)
          .alpha(HOVER_ALPHA)
          .alphaDecay(HOVER_ALPHA_DECAY)
          .alphaMin(0.001)
          .restart()
      } else if (rerender || needsLayout) {
        this.saveState = true
        // The drawn set changed for a reason the reader asked for — a switch, a search, a
        // selection, or the fleet itself — so the extent has too. Re-frame it NOW.
        //
        // ⚠⚠ NOW, NOT AT REST, AND THE DIFFERENCE IS TEN SECONDS OF WRONG SIZE. Waiting for the
        // simulation to cool means a cold load draws the graph at whatever scale the constructor
        // guessed, lets the reader start reading it, and THEN resizes the whole picture under
        // them with no input of their own — which is the most alarming thing on this page, and it
        // is what shipped in the first version of this fit.
        //
        // Seeded positions are good enough to frame from: the rings are fixed radii, so the
        // extent barely moves as the springs relax. Measured on the seventeen-node fleet, seeded
        // 535x532 against settled 516x552 — a 3.5% difference in the scale chosen. Waiting bought
        // 3.5% and cost the reader their orientation.
        this.viewport.centerGraph(nodes.filter(drawnNode), { animate: this.settled })
        const alpha = this.settled ? 1 : 0.5
        const decay = this.settled ? 0.02 : 0.01
        this.simulation
          .velocityDecay(LAYOUT_VELOCITY_DECAY)
          .alpha(alpha)
          .alphaDecay(decay)
          .alphaMin(0.001)
          .restart()
      } else if (activeIdChanged) {
        // The cursor moved but nothing came or went — a label, an active flag. Nudge it.
        this.simulation
          .velocityDecay(HOVER_VELOCITY_DECAY)
          .alpha(HOVER_ALPHA)
          .alphaDecay(HOVER_ALPHA_DECAY)
          .restart()
      }
    }

  updateSize(width: number, height: number) {
    this.viewport.updateSize(width, height, Array.from(this.adapter.nodeMap.values()).filter(drawnNode))
  }

  destroy() {
    // A pending trailing draw would otherwise run against a cleared SVG and a stopped simulation.
    this.update.cancel()
    clearTimeout(this.leaveTimeout)
    clearTimeout(this.settleTimeout)
    this.unpinTimeouts.forEach(timeout => clearTimeout(timeout))
    this.unpinTimeouts.clear()
    this.simulation.stop()
    this.removeAllListeners()
    this.svg.selectAll('*').remove()
    // ⚠⚠ TAKE BACK EVERY LISTENER THE CONSTRUCTOR PUT ON THE `<svg>`. The element is not this
    // engine's own: `Graph.tsx` gives the same one to the map engine (`mapCore.ts`) when the reader
    // turns on Show Map. A listener left here takes the events that come up from a map node. d3's
    // `wheel.zoom` stops the event before the map's own wheel listener, so the map did not zoom
    // with the cursor on a node; `mousedown.zoom` starts a pan of this dead graph, and at the
    // mouseup d3 suppresses the click when the pointer moved one pixel, so the node was not
    // selected. While a rebuild made another `GraphNetwork`, its constructor replaced each of
    // these and the defect could not show.
    this.svg.on('.zoom', null).on('dblclick', null).on('click', null)
  }

  private center() {
    this.viewport.centerGraph(Array.from(this.adapter.nodeMap.values()).filter(drawnNode))
  }

  /**
   * ⚠⚠ EVERY `.transition()` IN A DRAW IS PRECEDED BY `.interrupt()`, and the hang in an idle tab
   * is why. d3 runs transitions on `requestAnimationFrame`, which a hidden tab or a covered window
   * does not fire, while the draws keep coming (the task refreshes,
   * realtime events). Without the interrupt each draw queued one more transition per element, none
   * ever started, and the first frame after the tab came back worked the whole queue off in one
   * synchronous task — 400 queued draws on 30 elements blocked the page for 5.4 s, and the cost
   * grows faster than the square of the queue. With it each element holds at most one.
   */
  private updateNodes(data: GraphNode[]) {
    const nodes = this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point').data(data, d => d.id)

    // ⚠⚠ A LEAVING NODE STOPS TAKING THE POINTER FIRST. It fades for `TRANSITION_DURATION` and is
    // in the DOM the whole time, so a cursor crossing it during the fade raised `mouseenter` on
    // something that was on its way out — and the matching `mouseleave` never came, because the
    // element was removed instead. The highlight it had just set then had nothing left to clear
    // it, and stuck until the reader pointed at something else. Reachable whenever the pointer
    // moves fast, which is exactly when leaves are appearing and disappearing under it.
    nodes.exit().style('pointer-events', 'none').interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 0).remove()

    const nodeEnter = nodes
      .enter()
      .append('g')
      .attr('opacity', 0)
      .each((d, i, groups) => {
        const group = d3.select(groups[i])
        this.renderer.node(d, group)
      })
      .attr('transform', d => (state.loadPosition(d) ? `translate(${d.x},${d.y})` : null))
      .on('click', (event, d) => this.emit('click', d, event))
      .on('contextmenu', (event, d) => {
        event.preventDefault()
        this.emit('click', d, event)
      })
      .on('mouseenter', (event, d) => {
        clearTimeout(this.leaveTimeout)

        // Don't emit hover for collapsed expandable nodes (they shouldn't trigger expansion)
        if (d.expanded === false) return

        // For services, use composite ID; for others use model node ID
        const activeId = d.type === 'service' ? d.id : d.modelNodeId ?? d.id
        this.emit('enter', activeId, event)
      })
      .on('mouseleave', (event, d) => {
        // Delay leave event for nodes and services to allow hover bridge.
        if (d.type === 'node' || d.type === 'service') {
          this.leaveTimeout = setTimeout(() => {
            this.emit('leave', d, event)
          }, TRANSITION_DURATION)
        } else {
          this.emit('leave', d, event)
        }
      })
      .call(
        d3
          .drag<SVGGElement, GraphNode>()
          .on('start', this.dragStarted)
          .on('drag', this.dragged)
          .on('end', this.dragEnded)
      )

    nodes.each((d, i, groups) => {
      if (d.stale) {
        const group = d3.select(groups[i])
        group.selectAll('*').remove()
        this.renderer.node(d, group)
      }
    })

    const mergedNodes = nodeEnter.merge(nodes)
    // ⚠⚠ AND IT TAKES THE POINTER BACK IF IT RETURNS. `selectAll('.point')` matches elements that
    // are still fading out, so a node that leaves and comes back inside `TRANSITION_DURATION` —
    // point at a node, leave, point at it again — is re-joined as an UPDATE, and the new opacity
    // transition cancels the pending `.remove()`. It lives on, and without this line it would
    // keep the `pointer-events: none` its exit put there: permanently unhoverable, permanently
    // unclickable, and looking exactly like every other node.
    mergedNodes.style('pointer-events', null).interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 1)

    return mergedNodes
  }

  private updateLabels(data: GraphNode[]) {
    const nodes = this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label').data(data, d => d.id)

    nodes.exit().interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 0).remove()

    const nodeEnter = nodes
      .enter()
      .append('text')
      .attr('opacity', 0)
      .each((d, i, groups) => {
        const group = d3.select(groups[i])
        this.renderer.label(d, group)
      })

    nodes.each((d, i, groups) => {
      const group = d3.select(groups[i])
      if (d.stale) {
        group.selectAll('*').remove()
        this.renderer.label(d, group)
      }
    })

    const mergedNodes = nodeEnter.merge(nodes)
    mergedNodes.interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 1)

    return mergedNodes
  }

  private updateLinks(linkData: GraphLink[]) {
    const links = this.renderer.linkContainer
      .selectAll<SVGLineElement, GraphLink>('.link')
      .data(linkData, (d: GraphLink) => d.id)

    links.exit().interrupt().transition().duration(TRANSITION_DURATION).attr('stroke-opacity', 0).remove()

    const linkEnter = links
      .enter()
      .append('line')
      .each((d, i, groups) => {
        const line = d3.select(groups[i])
        this.renderer.link(d, line)
      })
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 0)

    links.each((d, i, lines) => {
      const line = d3.select(lines[i])
      if (d.stale) this.renderer.link(d, line)
    })

    const mergedLinks = linkEnter.merge(links)
    mergedLinks.interrupt().transition().duration(TRANSITION_DURATION).attr('stroke-opacity', 1)

    return mergedLinks
  }

  private updateBridges(linkData: GraphLink[]) {
    const BRIDGE_WIDTH = 24
    // Only bridge node→service links while services are expanded; that's the
    // path the cursor travels when moving from a hovered node out to a service.
    const bridgeData = linkData.filter(d => d.expanded && d.targetType === 'service')

    const bridges = this.renderer.bridgeContainer
      .selectAll<SVGLineElement, GraphLink>('.bridge')
      .data(bridgeData, (d: GraphLink) => d.id)

    bridges.exit().remove()

    bridges
      .enter()
      .append('line')
      .attr('class', 'bridge')
      .attr('stroke', 'transparent')
      .attr('stroke-width', BRIDGE_WIDTH)
      .attr('stroke-linecap', 'round')
      .style('pointer-events', 'stroke')
      .on('mouseenter', () => clearTimeout(this.leaveTimeout))
      .on('mouseleave', (event, d) => {
        this.leaveTimeout = setTimeout(() => {
          this.emit('leave', d.source as GraphNode, event)
        }, TRANSITION_DURATION)
      })
  }

  private updateParticles(linkData: GraphLink[]) {
    const MAX_PARTICLES = 50
    const MIN_PARTICLES = 1 // Minimum particles per line
    const BASE_SPACING = 120 // Base spacing between particles
    const MIN_SPACING = 8 // Minimum spacing between particles
    const BASE_SPEED = 50 // Base speed in pixels per second
    const MIN_BYTES = 1024 // 1KB minimum for speed calculation
    const MAX_BYTES = 1024 * 1024 * 1024 * 1024 // 1TB cap for speed calculation
    const MIN_SPEED_FACTOR = 0.2 // Slowest speed multiplier
    const MAX_SPEED_FACTOR = 1.0 // Fastest speed multiplier

    const streamingLinks = linkData.filter(d => d.streaming)

    const particleData = streamingLinks.flatMap(link => {
      const particles: GraphParticle[] = []

      // Calculate line length
      const source = link.source as GraphNode
      const target = link.target as GraphNode

      if (!source?.x || !source?.y || !target?.x || !target?.y) return particles

      const dx = target.x - source.x
      const dy = target.y - source.y
      const lineLength = Math.sqrt(dx * dx + dy * dy)

      const directions = [
        { type: 'rx', bytes: link.rxBytes, reverse: link.targetType === 'proxy' },
        { type: 'tx', bytes: link.txBytes, reverse: link.targetType === 'gateway' },
      ] as const

      for (const { type, bytes, reverse } of directions) {
        if (bytes) {
          // Clamp bytes to reasonable range for calculations
          const clampedBytes = Math.min(Math.max(bytes, MIN_BYTES), MAX_BYTES)

          // Calculate normalized byte factor (0-1)
          const byteFactor = Math.log2(clampedBytes / MIN_BYTES) / Math.log2(MAX_BYTES / MIN_BYTES)

          // Calculate particle count based primarily on byte rate
          const byteBasedCount = Math.ceil(MIN_PARTICLES + (MAX_PARTICLES - MIN_PARTICLES) * Math.pow(byteFactor, 0.5))

          // Adjust spacing based on byte rate - higher rates get closer spacing
          const minSpacing = Math.max(BASE_SPACING * (1 - byteFactor * 1.6), MIN_SPACING)

          // Calculate maximum particles that would fit with this spacing
          const maxParticlesForSpacing = Math.floor(lineLength / minSpacing)

          // Use the byte-based count, but don't exceed what would fit on the line
          const count = Math.min(maxParticlesForSpacing, byteBasedCount)

          // Speed scaling
          const speedFactor = MIN_SPEED_FACTOR + (MAX_SPEED_FACTOR - MIN_SPEED_FACTOR) * byteFactor
          const speed = BASE_SPEED * speedFactor

          for (let i = 0; i < count; i++) {
            particles.push({
              id: `${link.id}-${type}-${i}`,
              link,
              progress: i / count,
              reverse,
              speed,
              lineLength,
            })
          }
        }
      }

      return particles
    })

    const particles = this.renderer.particleContainer
      .selectAll<SVGCircleElement, GraphParticle>('.particle')
      .data(particleData, (d: GraphParticle) => d.id)

    particles.exit().interrupt().transition().duration(TRANSITION_DURATION).attr('opacity', 0).remove()

    const particleEnter = particles
      .enter()
      .append('circle')
      .attr('class', 'particle')
      .attr('r', 2)
      .attr('opacity', 0)
      .transition()
      .duration(TRANSITION_DURATION)
      .attr('opacity', 1)

    if (streamingLinks.length > 0) {
      this.renderer.startParticles()
    } else {
      this.renderer.stopParticles()
    }

    return particleEnter
  }

  private tick() {
    const links = this.renderer.linkContainer.selectAll<SVGLineElement, GraphLink>('.link')
    const bridges = this.renderer.bridgeContainer.selectAll<SVGLineElement, GraphLink>('.bridge')
    const nodes = this.renderer.nodeContainer.selectAll<SVGGElement, GraphNode>('.point')
    const labels = this.renderer.labelContainer.selectAll<SVGTextElement, GraphNode>('.label')

    const x1 = (d: GraphLink) => (d.source as GraphNode).x!
    const y1 = (d: GraphLink) => (d.source as GraphNode).y!
    const x2 = (d: GraphLink) => (d.target as GraphNode).x!
    const y2 = (d: GraphLink) => (d.target as GraphNode).y!

    links.attr('x1', x1).attr('y1', y1).attr('x2', x2).attr('y2', y2)
    bridges.attr('x1', x1).attr('y1', y1).attr('x2', x2).attr('y2', y2)

    const transform = (d: GraphNode) => `translate(${d.x},${d.y})`
    nodes.attr('transform', transform)
    labels.attr('transform', transform)
  }

  private dragStarted = (event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>, d: GraphNode) => {
    // ⚠ The friction too, and not only here for symmetry: a drag can only start after the pointer
    // entered the node, and that mouseenter took the hover branch and left `velocityDecay` at
    // `HOVER_VELOCITY_DECAY`. Every drag would otherwise run at hover friction — the permanent
    // sluggishness `force.ts` says is why both values are set on every restart.
    if (!event.active) this.simulation.velocityDecay(LAYOUT_VELOCITY_DECAY).alphaTarget(0.3).restart()
    d.fx = d.x
    d.fy = d.y
  }

  private dragged = (event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>, d: GraphNode) => {
    d.fx = event.x
    d.fy = event.y
  }

  private dragEnded = (event: d3.D3DragEvent<SVGGElement, GraphNode, GraphNode>, d: GraphNode) => {
    if (!event.active) this.simulation.alphaTarget(0)
    this.saveState = true
    // Don't unpin if this node is being held for its services
    if (!this.pinnedNodeIds.has(d.id)) {
      d.fx = null
      d.fy = null
    }
  }
}
